import type { ABTestVariant } from '../types/user.types';

const STORAGE_KEY = 'vocalistUserData';

const VALID_AB_TEST_VARIANTS = new Set<string>([
  'none',
  'purple',
  'modern',
  'palette-purple',
  'palette-blue',
  'palette-light',
  'palette-dg',
  'email-before-plan-a',
  'email-before-plan-b',
  'more-steps-a',
  'more-steps-b',
  'more-steps-a2',
  'more-steps-b2',
  'more-steps-c2',
  'happy-birthday-detection-control',
  'happy-birthday-detection-treatment',
  'bday-control',
  'bday-treatment-email-before',
  'bday-treatment-email-after',
  'paywall-v4',
  'paywall-v5',
  'paywall-v6',
  'pw-v4',
  'pw-v5',
  'pw-v6',
  'popunder-control',
  'popunder-old-landing',
  'yt-karaoke-control',
  'yt-karaoke-treatment',
]);

interface AssignmentStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface EarlyAbtestManagerOptions {
  getSearch?: () => string;
  getStorage?: () => AssignmentStorage;
  random?: () => number;
}

export type FunnelExperience = 'purple' | 'modern';

/**
 * Resolves experiments that must be known before the application bootstrap starts.
 *
 * The selected value is written into the same `vocalistUserData.abTestVariant` field
 * consumed by UserRepository. The in-memory result makes the resolution idempotent even
 * when browser storage is unavailable.
 */
export class EarlyAbtestManager {
  private assignment: ABTestVariant | null = null;
  private readonly getSearch: () => string;
  private readonly getStorage: () => AssignmentStorage;
  private readonly random: () => number;

  constructor(options: EarlyAbtestManagerOptions = {}) {
    this.getSearch = options.getSearch ?? (() => (
      typeof window === 'undefined' ? '' : window.location.search
    ));
    this.getStorage = options.getStorage ?? (() => localStorage);
    this.random = options.random ?? Math.random;
  }

  resolve(): ABTestVariant {
    if (this.assignment !== null) return this.assignment;

    const params = new URLSearchParams(this.getSearch());
    const storedData = this.readStoredData();
    const override = this.getValidOverride(params);

    let assignment: ABTestVariant;
    if (override) {
      assignment = override;
    } else if (typeof storedData.abTestVariant === 'string' && storedData.abTestVariant) {
      // Historical builds accepted arbitrary non-empty strings. Preserve such assignments
      // rather than silently moving an existing visitor into a different experiment.
      assignment = storedData.abTestVariant as ABTestVariant;
    } else if (this.hasInvalidOverride(params)) {
      // A non-empty QA override is intentional. Keep a fresh visitor in control rather
      // than silently enrolling them in a random experiment because of a typo.
      assignment = 'none';
    } else if (params.get('network') === 'popunder') {
      assignment = this.random() < 0.5 ? 'popunder-control' : 'popunder-old-landing';
    } else {
      // In-app YouTube-karaoke test: the funnel looks the same in both arms; the
      // mobile app reads the arm back from `/check` (via the checkout customData).
      assignment = this.random() < 0.5 ? 'yt-karaoke-control' : 'yt-karaoke-treatment';
    }

    // Set this before attempting storage so a failed write cannot trigger another draw.
    this.assignment = assignment;

    if (override || storedData.abTestVariant !== assignment) {
      this.persist(storedData, assignment);
    }

    return assignment;
  }

  getVariant(): ABTestVariant {
    return this.resolve();
  }

  getExperience(): FunnelExperience {
    return this.resolve() === 'modern' ? 'modern' : 'purple';
  }

  isModern(): boolean {
    return this.getExperience() === 'modern';
  }

  /**
   * Keep analytics and checkout attribution aligned with the UI if the optional Modern
   * bundle cannot load. This is called before UserRepository or analytics initialize.
   */
  usePurpleFallback(): ABTestVariant {
    const storedData = this.readStoredData();
    this.assignment = 'purple';
    this.persist(storedData, this.assignment);
    return this.assignment;
  }

  private getValidOverride(params: URLSearchParams): ABTestVariant | null {
    const abtest = params.get('abtest');
    if (abtest && VALID_AB_TEST_VARIANTS.has(abtest)) {
      return abtest as ABTestVariant;
    }

    const variant = params.get('variant');
    return variant && VALID_AB_TEST_VARIANTS.has(variant)
      ? variant as ABTestVariant
      : null;
  }

  private hasInvalidOverride(params: URLSearchParams): boolean {
    return ['abtest', 'variant'].some((name) => {
      const value = params.get(name);
      return Boolean(value && !VALID_AB_TEST_VARIANTS.has(value));
    });
  }

  private readStoredData(): Record<string, unknown> {
    try {
      const serialized = this.getStorage().getItem(STORAGE_KEY);
      if (!serialized) return {};

      const parsed: unknown = JSON.parse(serialized);
      return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : {};
    } catch {
      return {};
    }
  }

  private persist(storedData: Record<string, unknown>, assignment: ABTestVariant): void {
    try {
      this.getStorage().setItem(STORAGE_KEY, JSON.stringify({
        ...storedData,
        abTestVariant: assignment,
      }));
    } catch {
      // The page-lifetime assignment remains available from `this.assignment`.
    }
  }
}

const earlyAbtestManager = new EarlyAbtestManager();

export default earlyAbtestManager;
